import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  Typography,
  Grid,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import InfoHeader from '../components/InfoHeader';
import LoadingIndicator from '../components/LoadingIndicator';
import { auditoriaService } from '../services/AuditoriaService';
import { AuditLogDTO } from '../types/auditoria/AuditoriaTypes';
import toastService from '../utils/toastService';

interface CampoAlterado {
  campo: string;
  de: string;
  para: string;
}

const formatValor = (valor: unknown): string => {
  if (valor === null || valor === undefined) {
    return '—';
  }
  if (typeof valor === 'object') {
    return JSON.stringify(valor);
  }
  return String(valor);
};

/**
 * Faz o parse do JSON `camposAlterados` no formato { campo: { de, para } }.
 * Retorna null se o parse falhar (a página então exibe a string crua).
 */
const parseCamposAlterados = (raw: string): CampoAlterado[] | null => {
  if (!raw) {
    return [];
  }
  try {
    const obj = JSON.parse(raw) as Record<string, { de?: unknown; para?: unknown }>;
    if (!obj || typeof obj !== 'object') {
      return null;
    }
    return Object.entries(obj).map(([campo, alteracao]) => ({
      campo,
      de: formatValor(alteracao?.de),
      para: formatValor(alteracao?.para),
    }));
  } catch {
    return null;
  }
};

const formatDataHora = (iso: string): string => {
  if (!iso) {
    return '—';
  }
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? iso : data.toLocaleString('pt-BR');
};

const CamposAlteradosCell: React.FC<{ raw: string }> = ({ raw }) => {
  const campos = parseCamposAlterados(raw);

  if (campos === null) {
    // Parse falhou — exibir a string crua.
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {raw}
      </Typography>
    );
  }

  if (campos.length === 0) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {campos.map((c) => (
        <Typography key={c.campo} variant="body2">
          <strong>{c.campo}:</strong>{' '}
          <Box component="span" sx={{ color: 'text.secondary' }}>{c.de}</Box>
          {' → '}
          <Box component="span" sx={{ color: 'primary.main' }}>{c.para}</Box>
        </Typography>
      ))}
    </Box>
  );
};

const Auditoria: React.FC = () => {
  const [registros, setRegistros] = useState<AuditLogDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filtros aplicados (usados na busca).
  const [filtroEntidade, setFiltroEntidade] = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  // Valores dos campos do formulário (aplicados ao clicar em "Filtrar").
  const [campoEntidade, setCampoEntidade] = useState('');
  const [campoUsuario, setCampoUsuario] = useState('');
  const [campoDataInicio, setCampoDataInicio] = useState('');
  const [campoDataFim, setCampoDataFim] = useState('');

  const carregar = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await auditoriaService.listar(page, rowsPerPage, {
        entidade: filtroEntidade || undefined,
        usuarioGuid: filtroUsuario || undefined,
        dataInicio: filtroDataInicio || undefined,
        dataFim: filtroDataFim || undefined,
      });
      setRegistros(response.registros || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        toastService.error('Erro ao carregar o relatório de alterações.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, filtroEntidade, filtroUsuario, filtroDataInicio, filtroDataFim]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleFiltrar = () => {
    setPage(1);
    setFiltroEntidade(campoEntidade.trim());
    setFiltroUsuario(campoUsuario.trim());
    setFiltroDataInicio(campoDataInicio);
    setFiltroDataFim(campoDataFim);
  };

  const handleLimpar = () => {
    setCampoEntidade('');
    setCampoUsuario('');
    setCampoDataInicio('');
    setCampoDataFim('');
    setPage(1);
    setFiltroEntidade('');
    setFiltroUsuario('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
  };

  return (
    <Box sx={{ p: 2 }}>
      <InfoHeader
        title="Relatório de Alterações"
        message="Acompanhe quais usuários alteraram quais dados e quando, dentro da sua empresa."
        isImportant={false}
      />

      <Paper
        elevation={0}
        sx={{ p: 3, mt: 2, borderRadius: 3, border: '1px solid #e0e0e0' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <HistoryIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Auditoria
          </Typography>
        </Box>

        {/* Filtros */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Entidade"
              placeholder="Ex: Meta"
              fullWidth
              size="small"
              value={campoEntidade}
              onChange={(e) => setCampoEntidade(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Usuário"
              placeholder="Nome ou e-mail"
              fullWidth
              size="small"
              value={campoUsuario}
              onChange={(e) => setCampoUsuario(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Data início"
              type="date"
              fullWidth
              size="small"
              value={campoDataInicio}
              onChange={(e) => setCampoDataInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Data fim"
              type="date"
              fullWidth
              size="small"
              value={campoDataFim}
              onChange={(e) => setCampoDataFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleFiltrar}>
              Filtrar
            </Button>
            <Button variant="outlined" onClick={handleLimpar}>
              Limpar
            </Button>
          </Grid>
        </Grid>

        {isLoading ? (
          <LoadingIndicator message="Carregando relatório de alterações..." />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Entidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ação</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Data/Hora</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Campos alterados</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registros.map((registro) => (
                    <TableRow key={registro.guid}>
                      <TableCell>
                        <Chip label={registro.entidade} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{registro.acao}</TableCell>
                      <TableCell>{registro.usuarioNome || '—'}</TableCell>
                      <TableCell>{formatDataHora(registro.dataHoraGravacao)}</TableCell>
                      <TableCell sx={{ maxWidth: 480 }}>
                        <CamposAlteradosCell raw={registro.camposAlterados} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={Math.max(page - 1, 0)}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Linhas por página"
            />

            {registros.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  Nenhuma alteração registrada para os filtros selecionados.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Auditoria;
